// ViewManager.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  PermissionType,
  ViewType,
  STAT_WIDGETS_BY_TYPE,
  DEFAULT_POOL_BY_TYPE,
} from "../../../Enums";
import { decorateViewsWithPermissions } from "../../../permissions/Permissions";
import { Box, Button } from "@mui/material";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import SaveAltIcon   from "@mui/icons-material/SaveAlt";
import IosShareIcon  from "@mui/icons-material/IosShare";
import ViewsPane   from "./ViewsPane";
import ColumnsPane from "./ColumnsPane";
import PoolPane    from "./PoolPane";
import "./ViewManager.css";

const normalizeLinked = (v) => {
  const s = String(v || "").trim().toLowerCase();
  if (s === "users") return "user";
  return s || "other";
};

const NON_DATA_TYPES = new Set([
  "INFORMATION",
  "SUBMISSIONLIST",
  "SUMMARY",
  "BUTTON",
]);

const getViewsFromForm = (form) => {
  if (Array.isArray(form?.views))       return form.views;
  if (Array.isArray(form?.meta?.views)) return form.meta.views;
  return [];
};

const setViewsOnForm = (form, nextViews) => {
  if (Array.isArray(form?.views))
    return { ...form, views: nextViews };
  if (Array.isArray(form?.meta?.views))
    return { ...form, meta: { ...form.meta, views: nextViews } };
  return form;
};

const toFormsArr = (formData) => {
  if (Array.isArray(formData))                   return formData;
  if (formData && Array.isArray(formData.forms)) return formData.forms;
  return [];
};

const SLIDER_DELAY   = 500;
const DISCRETE_DELAY = 300;
const SLIDER_PROPS   = new Set(["width"]);

export default function ViewManager({
  formData = [],
  selectedViewLabel: selectedViewProp = null,
  isCustomiseViewOpen = false,
  appUser,
  viewTypes = [],
  onColumnsChange = () => {},
  onViewsChange,
  customTheme,
}) {
  const isSuperAdmin = appUser?.adminLevel === PermissionType.SUPER_ADMIN;
  const userId       = appUser?.id;

  const [expanded, setExpanded] = useState(false);
  useEffect(() => setExpanded(isCustomiseViewOpen), [isCustomiseViewOpen]);

  // ─── Local mirror of formData ─────────────────────────────────────────────
  const [localForms, setLocalForms] = useState(() => toFormsArr(formData));
  useEffect(() => {
    setLocalForms(toFormsArr(formData));
  }, [formData]);

  const patchViewFields = (formId, viewLabel, nextFields) => {
    setLocalForms((prev) =>
      prev.map((form) => {
        if (String(form.id) !== String(formId)) return form;
        const nextViews = getViewsFromForm(form).map((v) =>
          String(v.viewLabel) === String(viewLabel)
            ? { ...v, fields: nextFields }
            : v
        );
        return setViewsOnForm(form, nextViews);
      })
    );
  };

  const patchFormViews = (formId, nextViews) => {
    setLocalForms((prev) =>
      prev.map((form) =>
        String(form.id) === String(formId)
          ? setViewsOnForm(form, nextViews)
          : form
      )
    );
  };

  // ─── Derived views ────────────────────────────────────────────────────────
  const flatViews = useMemo(() => {
    const out = [];
    localForms.forEach((f) => {
      getViewsFromForm(f).forEach((v) => {
        out.push({ ...v, formId: f.id, projectToken: f.projectToken });
      });
    });
    return out;
  }, [localForms]);

  const getFormViews = (formId) => {
    const form = localForms.find((f) => f.id === formId);
    return getViewsFromForm(form);
  };

  const setFormViews = async (formId, nextViews) => {
    if (typeof onViewsChange !== "function") {
      console.error("ViewManager: onViewsChange(formId, updatedViews) is required.");
      return { error: "onViewsChange missing" };
    }
    patchFormViews(formId, nextViews);
    try {
      await onViewsChange(formId, nextViews);
      return { ok: true };
    } catch (e) {
      console.error("onViewsChange failed:", e);
      setLocalForms(toFormsArr(formData));
      return { error: e?.message || String(e) };
    }
  };

  // Stabilize decoratedViews reference — guard with JSON equality check
  const prevDecoratedRef = useRef([]);

  const decoratedViews = useMemo(() => {
    const next = decorateViewsWithPermissions(flatViews, { userId, isSuperAdmin });
    if (JSON.stringify(next) === JSON.stringify(prevDecoratedRef.current)) {
      return prevDecoratedRef.current;
    }
    prevDecoratedRef.current = next;
    return next;
  }, [flatViews, userId, isSuperAdmin]);

  // ─── Selection ────────────────────────────────────────────────────────────
  const [selectedKey, setSelectedKey] = useState({ formId: "", viewLabel: "" });
  const hasInitializedSelection = useRef(false);

  useEffect(() => {
    if (hasInitializedSelection.current) return;
    if (decoratedViews.length === 0) return;

    const incomingLabel =
      typeof selectedViewProp === "string"
        ? selectedViewProp
        : selectedViewProp?.viewLabel;

    if (!incomingLabel) {
      const first = decoratedViews[0];
      if (first) {
        setSelectedKey({ formId: first.formId, viewLabel: first.viewLabel });
        hasInitializedSelection.current = true;
      }
      return;
    }

    const match = decoratedViews.find(
      (v) => String(v.viewLabel || "") === String(incomingLabel)
    );
    if (match) {
      setSelectedKey({ formId: match.formId, viewLabel: match.viewLabel });
      hasInitializedSelection.current = true;
    }
  }, [decoratedViews]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setSelectedKey((prev) => {
      if (!prev.viewLabel || !prev.formId) return prev;
      const exists = decoratedViews.some(
        (v) => v.formId === prev.formId && v.viewLabel === prev.viewLabel
      );
      if (exists) return prev;
      const fallback = decoratedViews[0]
        ? { formId: decoratedViews[0].formId, viewLabel: decoratedViews[0].viewLabel }
        : { formId: "", viewLabel: "" };
      return fallback;
    });
  }, [decoratedViews]);

  const activeView = useMemo(
    () =>
      decoratedViews.find(
        (v) => v.formId === selectedKey.formId && v.viewLabel === selectedKey.viewLabel
      ) || null,
    [decoratedViews, selectedKey.formId, selectedKey.viewLabel]
  );

  // ─── Helpers ──────────────────────────────────────────────────────────────
  const nextUniqueLabel = (formId, base) => {
    const labels = new Set(
      getFormViews(formId).map((v) => String(v.viewLabel || ""))
    );
    if (!labels.has(base))             return base;
    if (!labels.has(`${base} (copy)`)) return `${base} (copy)`;
    let n = 2;
    while (labels.has(`${base} (copy ${n})`)) n += 1;
    return `${base} (copy ${n})`;
  };

  const normalizeIndex = (cols) => cols.map((c, i) => ({ ...c, index: i + 1 }));

  // ─── View-level operations ────────────────────────────────────────────────
  const createOrCloneView = async ({ formId, sourceView, label, type, makeDefault = false }) => {
    if (!formId) return null;
    const now     = new Date().toISOString();
    const current = getFormViews(formId);
    let newView;

    if (sourceView) {
      const src = current.find((v) => v.viewLabel === sourceView.viewLabel);
      if (!src) return null;
      const newLabel = nextUniqueLabel(formId, src.viewLabel);
      newView = {
        ...src,
        viewLabel:   newLabel,
        defaultView: false,
        read:        userId,
        write:       userId,
        createdBy:   userId,
        createdAt:   now,
        updatedAt:   now,
        version:     (Number(src.version) || 1) + 1,
      };
    } else {
      const safeLabel = nextUniqueLabel(formId, label);
      newView = {
        viewLabel:   safeLabel,
        type:        type || ViewType.SUBMISSIONS,
        fields:      [],
        defaultView: !!makeDefault,
        statWidget:
          STAT_WIDGETS_BY_TYPE?.[type] ||
          STAT_WIDGETS_BY_TYPE?.[ViewType.SUBMISSIONS] ||
          [],
        read:      userId,
        write:     userId,
        createdBy: userId,
        createdAt: now,
        updatedAt: now,
        version:   1,
      };
    }

    let next = [...current, newView];
    if (newView.defaultView || makeDefault) {
      next = next.map((v) => ({
        ...v,
        defaultView: v.viewLabel === newView.viewLabel,
      }));
    }

    const res = await setFormViews(formId, next);
    if (res?.ok) {
      setSelectedKey({ formId, viewLabel: newView.viewLabel });
      return newView;
    }
    return null;
  };

  const ensureEditableThen = async (view, applyEdit) => {
    if (!view) return;
    if (!view._locked) return applyEdit(view);
    const ok = window.confirm(
      "This view is read-only. Do you want to create a personal copy and apply your changes there?"
    );
    if (!ok) return;
    const cloned = await createOrCloneView({ formId: view.formId, sourceView: view });
    if (!cloned) return;
    return applyEdit({ ...cloned, projectToken: view.projectToken, _locked: false });
  };

  const handleColumnsChange = async (nextColumns, formId, viewLabel, projectToken) => {
    patchViewFields(formId, viewLabel, nextColumns);
    await onColumnsChange(nextColumns, formId, viewLabel, projectToken);
  };

  const handleCreateView = async ({ label, type, makeDefault }) => {
    const formId = activeView?.formId || localForms?.[0]?.id;
    if (!formId) return;
    await createOrCloneView({ formId, label, type, makeDefault });
  };

  const handleDeleteView = async () => {
    const v = activeView;
    if (!v) return;
    if (v._isSystem) return console.warn("System views cannot be deleted.");
    if (v._locked)   return console.warn("You do not have permission to delete this view.");
    const current = getFormViews(v.formId);
    const next    = current.filter((x) => x.viewLabel !== v.viewLabel);
    const res     = await setFormViews(v.formId, next);
    if (res?.ok) {
      const fallback = next[0]
        ? { formId: v.formId, viewLabel: next[0].viewLabel }
        : { formId: "", viewLabel: "" };
      setSelectedKey(fallback);
    }
  };

  const handleSetDefault = async () => {
    if (!activeView) return;
    await ensureEditableThen(activeView, async (editable) => {
      const current = getFormViews(editable.formId);
      const next    = current.map((v) => ({
        ...v,
        defaultView: v.viewLabel === editable.viewLabel,
      }));
      const res = await setFormViews(editable.formId, next);
      if (res?.ok)
        setSelectedKey({ formId: editable.formId, viewLabel: editable.viewLabel });
    });
  };

  const handleRenameView = async (key, newLabel) => {
    const formId   = key?.formId;
    const oldLabel = String(key?.viewLabel || "").trim();
    const desired  = String(newLabel || "").trim();
    if (!formId || !oldLabel || !desired) return;
    if (desired === oldLabel) return;

    const target = decoratedViews.find(
      (v) => v.formId === formId && String(v?.viewLabel || "").trim() === oldLabel
    );
    if (!target) return;

    await ensureEditableThen(target, async (editable) => {
      const current  = getFormViews(editable.formId);
      const oldExact = String(editable.viewLabel || "");
      const labels   = new Set(
        current
          .map((v) => String(v?.viewLabel || ""))
          .filter((l) => l && l !== oldExact)
      );
      let finalLabel = desired;
      if (labels.has(finalLabel)) {
        finalLabel = `${desired} (copy)`;
        if (labels.has(finalLabel)) {
          let n = 2;
          while (labels.has(`${desired} (copy ${n})`)) n += 1;
          finalLabel = `${desired} (copy ${n})`;
        }
      }
      const next = current.map((v) =>
        String(v?.viewLabel || "") === oldExact ? { ...v, viewLabel: finalLabel } : v
      );
      const res = await setFormViews(editable.formId, next);
      if (!res?.ok) return;
      if (
        selectedKey?.formId    === editable.formId &&
        selectedKey?.viewLabel === oldExact
      ) {
        setSelectedKey({ formId: editable.formId, viewLabel: finalLabel });
      }
    });
  };

  // ─── Column / field operations ────────────────────────────────────────────
  const fieldSaveTimers = useRef({});

  const scheduleFieldSave = (timerKey, delay, applyFn) => {
    clearTimeout(fieldSaveTimers.current[timerKey]);
    fieldSaveTimers.current[timerKey] = setTimeout(applyFn, delay);
  };

  const requestRename = (field, headerName) => {
    const apply = async () => {
      if (!activeView) return;
      await ensureEditableThen(activeView, async (editable) => {
        const ordered = (editable.fields || [])
          .slice()
          .sort((a, b) => (a.index ?? 0) - (b.index ?? 0));
        const next = ordered.map((c) =>
          c.field === field ? { ...c, headerName } : c
        );
        await handleColumnsChange(
          normalizeIndex(next),
          editable.formId,
          editable.viewLabel,
          editable.projectToken
        );
      });
    };
    scheduleFieldSave(`${field}::headerName`, DISCRETE_DELAY, apply);
  };

  const requestFieldChange = (fieldName, propKey, newValue) => {
    const apply = async () => {
      if (!activeView) return;
      await ensureEditableThen(activeView, async (editable) => {
        const next = (editable.fields || []).map((f) =>
          String(f.field) === String(fieldName)
            ? { ...f, [propKey]: newValue }
            : f
        );
        await handleColumnsChange(
          next,
          editable.formId,
          editable.viewLabel,
          editable.projectToken
        );
      });
    };
    const delay = SLIDER_PROPS.has(propKey) ? SLIDER_DELAY : DISCRETE_DELAY;
    scheduleFieldSave(`${fieldName}::${propKey}`, delay, apply);
  };

  const requestReorder = async (orderedCols) => {
    if (!activeView) return;
    await ensureEditableThen(activeView, async (editable) => {
      await handleColumnsChange(
        normalizeIndex(orderedCols),
        editable.formId,
        editable.viewLabel,
        editable.projectToken
      );
    });
  };

  const requestRemove = async (field) => {
    if (!activeView) return;
    await ensureEditableThen(activeView, async (editable) => {
      const next = (editable.fields || []).filter((c) => c.field !== field);
      await handleColumnsChange(
        normalizeIndex(next),
        editable.formId,
        editable.viewLabel,
        editable.projectToken
      );
    });
  };

  const requestAddFromPool = async (colLike) => {
    if (!activeView) return;
    await ensureEditableThen(activeView, async (editable) => {
      const exists = (editable.fields || []).some((c) => c.field === colLike.field);
      if (exists) return;
      const next = [...(editable.fields || []), { ...colLike, visible: true }];
      await handleColumnsChange(
        normalizeIndex(next),
        editable.formId,
        editable.viewLabel,
        editable.projectToken
      );
    });
  };

  // ─── Field JSON drawer ────────────────────────────────────────────────────
  const requestEditFieldJson = async () => {
    if (!activeView) return false;
    let allowed = false;
    await ensureEditableThen(activeView, async () => { allowed = true; });
    return allowed;
  };

  const applyFieldJson = async (fieldName, nextFieldObj) => {
    if (!activeView) return;
    const targetField = String(fieldName || "");
    if (!targetField) return;
    await ensureEditableThen(activeView, async (editable) => {
      const next = (editable.fields || []).map((c) =>
        String(c?.field || "") === targetField ? nextFieldObj : c
      );
      await handleColumnsChange(
        next,
        editable.formId,
        editable.viewLabel,
        editable.projectToken
      );
    });
  };

  // ─── Pool ─────────────────────────────────────────────────────────────────
  const activeLinked = useMemo(() => {
    const t = activeView?.type;
    if (t === ViewType.REVIEWS) return "review";
    if (t === ViewType.USERS)   return "user";
    return "submission";
  }, [activeView]);

  const activeFieldKeySet = useMemo(
    () => new Set((activeView?.fields || []).map((c) => `${activeLinked}::${c.field}`)),
    [activeView, activeLinked]
  );

  const poolRaw = useMemo(() => {
    const byKey = new Map();
    const keyOf = (p) => `${normalizeLinked(p.linked)}::${p.field}`;

    Object.entries(DEFAULT_POOL_BY_TYPE || {}).forEach(([, items]) => {
      (items || []).forEach((p) => {
        if (!p?.field) return;
        byKey.set(keyOf({ ...p, linked: normalizeLinked(p.linked) }), {
          ...p,
          origin: "hardcoded",
          linked: normalizeLinked(p.linked),
        });
      });
    });

    (localForms || []).forEach((form) => {
      const vs    = getViewsFromForm(form);
      const types = vs.map((v) => v?.type);
      const formTypeLinked = normalizeLinked(form?.type);
      const linked =
        formTypeLinked !== "other"
          ? formTypeLinked
          : types.includes(ViewType.SUBMISSIONS) ||
            types.includes(ViewType.SUBMISSIONS_UNDER_REVIEW)
          ? "submission"
          : types.includes(ViewType.REVIEWS)
          ? "review"
          : types.includes(ViewType.USERS)
          ? "user"
          : "submission";

      (form?.pages || []).forEach((p) => {
        (p?.controls || []).forEach((c) => {
          const field = typeof c?.key === "string" ? c.key.trim() : "";
          if (!field) return;
          const t = String(c?.type || "").toUpperCase();
          if (NON_DATA_TYPES.has(t)) return;
          const k = `${linked}::${field}`;
          if (byKey.has(k)) return;
          byKey.set(k, {
            field,
            dataSource:   `data.${field}`,
            headerName:   field,
            columnType:   "string",
            visible:      true,
            editable:     true,
            width:        150,
            origin:       "pages",
            linked,
            sourceFormId: form.id,
          });
        });
      });
    });

    return Array.from(byKey.values());
  }, [localForms]);

  const poolFiltered = useMemo(() => {
    const activeFormId = activeView?.formId;
    return poolRaw
      .map((p) => {
        const pLinked = normalizeLinked(p?.linked);
        const key     = `${pLinked}::${p.field}`;
        const missingInView =
          p?.origin === "pages" &&
          !!activeFormId &&
          p?.sourceFormId === activeFormId &&
          pLinked === activeLinked &&
          !activeFieldKeySet.has(key);
        return { ...p, missingInView };
      })
      .filter((p) => {
        const pLinked = normalizeLinked(p?.linked);
        const key     = `${pLinked}::${p.field}`;
        return !activeFieldKeySet.has(key);
      });
  }, [poolRaw, activeView, activeLinked, activeFieldKeySet]);

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div
      className={`ViewManager-container ${expanded ? "container-expanded" : ""}`}
      style={{
        "--vm-font-family": customTheme?.fontFamily,
        "--vm-font-size":   customTheme?.fontSize,
      }}
    >
      <div className="buttons">
        <Button title="Reset"><AutorenewIcon /></Button>
        <Button title="Save (dummy)"><SaveAltIcon /></Button>
        <Button title="Share (dummy)"><IosShareIcon /></Button>
      </div>

      <Box className="cv-root fields">
        <ViewsPane
          views={decoratedViews}
          selected={selectedKey}
          onSelect={setSelectedKey}
          onCreate={handleCreateView}
          onDelete={handleDeleteView}
          onSetDefault={handleSetDefault}
          onRenameView={handleRenameView}
          viewTypes={viewTypes}
          userId={userId}
          isSuperAdmin={isSuperAdmin}
          customTheme={customTheme}
        />

        <ColumnsPane
          view={activeView}
          fields={(activeView?.fields || [])
            .slice()
            .sort((a, b) => (a.index ?? 0) - (b.index ?? 0))}
          onRename={requestRename}
          onFieldChange={requestFieldChange}
          onRemoveToPool={requestRemove}
          onReorder={requestReorder}
          onEditJson={requestEditFieldJson}
          onApplyFieldJson={applyFieldJson}
          overlayRootId="vm-overlay-root"
          customTheme={customTheme}
        />

        <PoolPane
          pool={poolFiltered}
          onAddToView={requestAddFromPool}
          customTheme={customTheme}
          activeView={activeView}
        />
      </Box>

      <div id="vm-overlay-root" className="vm-overlay-root" />
    </div>
  );
}